import { Link } from '@tanstack/react-router';
import { ExternalLink } from 'lucide-react';
import { useMemo, useState } from 'react';

import { LLM_SPEND_TIMEZONE } from '@apps/web/components/admin/llm_spend/constants';
import {
    formatUsd,
    ratio,
} from '@apps/web/components/admin/llm_spend/spend_math';
import { Badge } from '@apps/web/components/ui/badge';
import {
    Card,
    CardAction,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@apps/web/components/ui/card';
import { DataTable } from '@apps/web/components/ui/data_table';
import { AdminToggle } from '@apps/web/features/admin_design/admin_controls';
import { formatDateAndTimeWithTz } from '@apps/web/lib/date_utils';
import { type ColumnDef, type RowData } from '@apps/web/lib/table';
import {
    type LlmSpendOrganizationRow,
    type LlmSpendOutlierRow,
    type LlmSpendOutreachResponseOutcomeRow,
    type LlmSpendOutreachStatusRow,
    type LlmSpendSchedulerOrganizationRow,
    type LlmSpendSchedulerOutlierRow,
    type LlmSpendSchedulerUserRow,
    type LlmSpendUserRow,
} from '@libraries/typescript/api_client';

function TableEmpty({ children }: { readonly children: string }) {
    return (
        <div className="text-muted-foreground px-4 py-8 text-center text-sm">
            {children}
        </div>
    );
}

// Internal names are how the team refers to customers (franchise orgs share
// a public name), so the table shows the internal name and only falls back
// to the public name when none is set.
function OrganizationName({
    id,
    name,
    internalName,
}: {
    readonly id: string;
    readonly name: string;
    readonly internalName: string | null;
}) {
    return (
        <Link
            to="/admin/organizations/$organization-id"
            params={{ 'organization-id': id }}
            className="block max-w-80 truncate font-medium whitespace-normal hover:underline"
        >
            {internalName ?? name}
        </Link>
    );
}

export function TopOrganizationsTable({
    rows,
    totalOrganizationCount,
}: {
    readonly rows: Array<LlmSpendOrganizationRow>;
    readonly totalOrganizationCount: number;
}) {
    const columns = useMemo<Array<ColumnDef<LlmSpendOrganizationRow>>>(
        () => [
            {
                accessorKey: 'organization_name',
                header: 'Organization',
                cell: ({ row }) => (
                    <OrganizationName
                        id={row.original.organization_id}
                        name={row.original.organization_name}
                        internalName={row.original.organization_internal_name}
                    />
                ),
            },
            {
                accessorKey: 'tracked_estimated_spend_usd',
                header: () => <div className="text-right">Spend</div>,
                cell: ({ row }) => (
                    <div className="text-right tabular-nums">
                        {formatUsd(row.original.tracked_estimated_spend_usd)}
                    </div>
                ),
            },
            {
                accessorKey: 'outreach_count',
                header: () => <div className="text-right">Outreaches</div>,
                cell: ({ row }) => (
                    <div className="text-right tabular-nums">
                        {row.original.outreach_count.toLocaleString()}
                    </div>
                ),
            },
            {
                id: 'cost_per_outreach',
                header: () => <div className="text-right">Per outreach</div>,
                cell: ({ row }) => (
                    <div className="text-right tabular-nums">
                        {formatUsd(
                            ratio(
                                row.original.outreach_spend_usd,
                                row.original.outreach_count,
                            ),
                        )}
                    </div>
                ),
            },
        ],
        [],
    );
    return (
        <Card>
            <CardHeader>
                <CardTitle className="text-sm">Top organizations</CardTitle>
                <CardDescription className="text-xs">
                    Highest-spend {rows.length.toLocaleString()} of{' '}
                    {totalOrganizationCount.toLocaleString()} organizations with
                    tracked spend.
                </CardDescription>
            </CardHeader>
            <CardContent>
                {rows.length === 0 ? (
                    <TableEmpty>
                        No organization spend in this range.
                    </TableEmpty>
                ) : (
                    <DataTable columns={columns} data={rows} pageSize={10} />
                )}
            </CardContent>
        </Card>
    );
}

type UserSpendRow = LlmSpendUserRow | LlmSpendSchedulerUserRow;

function UserTable({
    rows,
    totalUserCount,
    scheduler,
}: {
    readonly rows: Array<UserSpendRow>;
    readonly totalUserCount: number;
    readonly scheduler: boolean;
}) {
    const [hideAutomation, setHideAutomation] = useState(true);
    const visibleRows = useMemo(
        () =>
            hideAutomation ? rows.filter((row) => !row.is_automation) : rows,
        [hideAutomation, rows],
    );
    const columns = useMemo<Array<ColumnDef<UserSpendRow>>>(() => {
        const result: Array<ColumnDef<UserSpendRow>> = [
            {
                accessorKey: 'email',
                header: 'User',
                cell: ({ row }) => (
                    <div className="flex max-w-96 min-w-0 flex-col whitespace-normal">
                        <span className="flex items-center gap-2 font-medium">
                            <span className="truncate">
                                {row.original.email}
                            </span>
                            {row.original.is_automation ? (
                                <Badge variant="secondary">Automation</Badge>
                            ) : null}
                        </span>
                        <span className="text-muted-foreground truncate text-xs">
                            {row.original.organization_name}
                        </span>
                    </div>
                ),
            },
            {
                accessorKey: 'tracked_estimated_spend_usd',
                header: () => <div className="text-right">Spend</div>,
                cell: ({ row }) => (
                    <div className="text-right tabular-nums">
                        {formatUsd(row.original.tracked_estimated_spend_usd)}
                    </div>
                ),
            },
        ];
        if (scheduler) {
            result.push({
                id: 'scheduler_activity',
                header: () => <div className="text-right">Activity</div>,
                cell: ({ row }) => {
                    const schedulerRow =
                        row.original as LlmSpendSchedulerUserRow;
                    return (
                        <div className="text-right tabular-nums">
                            <div>
                                {schedulerRow.conversation_count.toLocaleString()}{' '}
                                conversations
                            </div>
                            <div className="text-muted-foreground text-xs">
                                {schedulerRow.llm_call_count.toLocaleString()}{' '}
                                calls ·{' '}
                                {schedulerRow.wake_count.toLocaleString()} wakes
                            </div>
                        </div>
                    );
                },
            });
        } else {
            result.push({
                accessorKey: 'llm_call_count',
                header: () => <div className="text-right">LLM calls</div>,
                cell: ({ row }) => (
                    <div className="text-right tabular-nums">
                        {row.original.llm_call_count.toLocaleString()}
                    </div>
                ),
            });
        }
        return result;
    }, [scheduler]);
    return (
        <Card>
            <CardHeader>
                <CardTitle className="text-sm">
                    {scheduler ? 'Scheduler users' : 'Top users'}
                </CardTitle>
                <CardDescription className="text-xs">
                    Highest-spend {visibleRows.length.toLocaleString()} of{' '}
                    {totalUserCount.toLocaleString()} users with tracked spend.
                </CardDescription>
                <CardAction>
                    <label className="text-muted-foreground flex items-center gap-1 text-xs">
                        Hide automation
                        <AdminToggle
                            checked={hideAutomation}
                            onCheckedChange={setHideAutomation}
                        />
                    </label>
                </CardAction>
            </CardHeader>
            <CardContent>
                {visibleRows.length === 0 ? (
                    <TableEmpty>No user spend in this range.</TableEmpty>
                ) : (
                    <DataTable
                        columns={columns}
                        data={visibleRows}
                        pageSize={10}
                    />
                )}
            </CardContent>
        </Card>
    );
}

export function TopUsersTable({
    rows,
    totalUserCount,
}: {
    readonly rows: Array<LlmSpendUserRow>;
    readonly totalUserCount: number;
}) {
    return (
        <UserTable
            rows={rows}
            totalUserCount={totalUserCount}
            scheduler={false}
        />
    );
}

// Plain-language meaning of each outreach status. Filled, unfilled, and
// cancelled are terminal; the rest can still change.
const STATUS_DETAILS: Readonly<
    Record<string, { readonly label: string; readonly description: string }>
> = {
    pending: {
        label: 'Pending',
        description: 'Created, but no caregiver has been contacted yet.',
    },
    paused: {
        label: 'Paused',
        description: 'Contacting is stopped for now and can resume.',
    },
    in_progress: {
        label: 'In progress',
        description: 'Still contacting caregivers.',
    },
    filled: {
        label: 'Filled',
        description: 'Every shift in the outreach got a caregiver.',
    },
    unfilled: {
        label: 'Unfilled',
        description:
            'A shift started with no caregiver, so the outreach ended. Other shifts in it may still have been filled.',
    },
    cancelled: {
        label: 'Cancelled',
        description:
            'A coordinator or the EHR cancelled the outreach or its shifts before every shift was resolved. Some shifts may have been filled first.',
    },
};

const OUTCOME_DETAILS: Readonly<
    Record<string, { readonly label: string; readonly description: string }>
> = {
    cancelled: {
        label: 'Cancelled',
        description:
            'A coordinator or the EHR cancelled the outreach or its shifts before every shift was resolved.',
    },
    filled: {
        label: 'Filled',
        description: 'Every shift in the outreach got a caregiver.',
    },
    ended_with_fill: {
        label: 'Ended, at least one shift filled',
        description:
            'Any terminal outreach where at least one shift was assigned, by anyone.',
    },
    ended_without_fill: {
        label: 'Ended, nothing filled',
        description:
            'Any terminal outreach where no shift was ever assigned. Includes cancellations with no fill.',
    },
};

function statusLabel(status: string | null): string {
    if (status === null) {
        return '—';
    }
    return STATUS_DETAILS[status]?.label ?? status.replace(/_/g, ' ');
}

function TraceLink({ id }: { readonly id: string }) {
    return (
        <Link
            to="/admin/agent-traces/$conversationId"
            params={{ conversationId: id }}
            className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-xs"
        >
            Trace
            <ExternalLink className="size-3" />
        </Link>
    );
}

export function OutliersTable({
    rows,
}: {
    readonly rows: Array<LlmSpendOutlierRow>;
}) {
    const columns = useMemo<Array<ColumnDef<LlmSpendOutlierRow>>>(
        () => [
            {
                accessorKey: 'organization_name',
                header: 'Conversation',
                cell: ({ row }) => (
                    <div className="flex flex-col whitespace-normal">
                        <span className="font-medium">
                            {row.original.organization_name}
                        </span>
                        <span className="text-muted-foreground text-xs tabular-nums">
                            {formatDateAndTimeWithTz(
                                row.original.started_at,
                                LLM_SPEND_TIMEZONE,
                            )}
                        </span>
                    </div>
                ),
            },
            {
                accessorKey: 'tracked_estimated_spend_usd',
                header: () => <div className="text-right">Cost</div>,
                cell: ({ row }) => (
                    <div className="flex items-center justify-end gap-2 tabular-nums">
                        {formatUsd(row.original.tracked_estimated_spend_usd)}
                        {row.original.cost_capped ? (
                            <Badge variant="destructive">Hit cap</Badge>
                        ) : null}
                    </div>
                ),
            },
            {
                accessorKey: 'status',
                header: 'Status',
                cell: ({ row }) => statusLabel(row.original.status),
            },
            {
                accessorKey: 'llm_call_count',
                header: () => <div className="text-right">LLM calls</div>,
                cell: ({ row }) => (
                    <div className="text-right tabular-nums">
                        {row.original.llm_call_count.toLocaleString()}
                    </div>
                ),
            },
            {
                id: 'trace',
                header: '',
                cell: ({ row }) => (
                    <TraceLink id={row.original.conversation_id} />
                ),
            },
        ],
        [],
    );
    return (
        <SpendTableCard
            title="Outlier conversations"
            description="Outreach conversations over $10, most expensive first."
            empty="No conversations over $10 in this range."
            rows={rows}
            columns={columns}
        />
    );
}

const RESPONSE_OUTCOME_DETAILS: Readonly<
    Record<
        LlmSpendOutreachResponseOutcomeRow['outcome'],
        { readonly label: string; readonly description: string }
    >
> = {
    yes: {
        label: 'At least one yes',
        description:
            'A caregiver explicitly responded that they were available and interested in at least one shift.',
    },
    responded_no_yes: {
        label: 'Responses, no yes',
        description:
            'A caregiver responded about at least one shift, but none said they were available and interested.',
    },
    no_response: {
        label: 'No recorded response',
        description: 'No caregiver response was recorded for any shift.',
    },
    untracked: {
        label: 'Response data unavailable',
        description:
            'Historical response facts were unavailable when this cost row was preserved.',
    },
};

const RESPONSE_PERCENT = new Intl.NumberFormat('en-US', {
    style: 'percent',
    maximumFractionDigits: 1,
});

export function OutreachResponseOutcomesTable({
    rows,
}: {
    readonly rows: Array<LlmSpendOutreachResponseOutcomeRow>;
}) {
    const trackedRows = rows.filter(
        (row) => row.outcome !== 'untracked' && row.outreach_count > 0,
    );
    const trackedOutreachCount = trackedRows.reduce(
        (total, row) => total + row.outreach_count,
        0,
    );
    const excludedOutreachCount = rows.reduce(
        (total, row) =>
            row.outcome === 'untracked' ? total + row.outreach_count : total,
        0,
    );
    const columns = useMemo<
        Array<ColumnDef<LlmSpendOutreachResponseOutcomeRow>>
    >(
        () => [
            {
                accessorKey: 'outcome',
                header: 'Caregiver response outcome',
                cell: ({ row }) => (
                    <div className="flex flex-col whitespace-normal">
                        <span className="font-medium">
                            {
                                RESPONSE_OUTCOME_DETAILS[row.original.outcome]
                                    .label
                            }
                        </span>
                        <span className="text-muted-foreground max-w-xl text-xs">
                            {
                                RESPONSE_OUTCOME_DETAILS[row.original.outcome]
                                    .description
                            }
                        </span>
                    </div>
                ),
            },
            {
                id: 'share_of_tracked',
                accessorFn: (row) =>
                    ratio(row.outreach_count, trackedOutreachCount),
                header: () => <div className="text-right">Share</div>,
                cell: ({ row }) => {
                    const share = ratio(
                        row.original.outreach_count,
                        trackedOutreachCount,
                    );
                    return (
                        <div className="text-right tabular-nums">
                            {share === null
                                ? '—'
                                : RESPONSE_PERCENT.format(share)}
                        </div>
                    );
                },
            },
            {
                accessorKey: 'outreach_count',
                header: () => <div className="text-right">Outreaches</div>,
                cell: ({ row }) => (
                    <div className="text-right tabular-nums">
                        {row.original.outreach_count.toLocaleString()}
                    </div>
                ),
            },
            {
                accessorKey: 'shift_count',
                header: () => <div className="text-right">Total shifts</div>,
                cell: ({ row }) => (
                    <div className="flex flex-col items-end text-right tabular-nums">
                        <span>{row.original.shift_count.toLocaleString()}</span>
                        {row.original.outcome === 'yes' ? (
                            <span className="text-muted-foreground text-xs">
                                {row.original.yes_shift_count.toLocaleString()}{' '}
                                {row.original.yes_shift_count === 1
                                    ? 'shift'
                                    : 'shifts'}{' '}
                                with yes
                            </span>
                        ) : null}
                    </div>
                ),
            },
            {
                id: 'cost_per_outreach',
                accessorFn: (row) =>
                    ratio(row.tracked_estimated_spend_usd, row.outreach_count),
                header: () => <div className="text-right">Cost/outreach</div>,
                cell: ({ row }) => (
                    <div className="text-right tabular-nums">
                        {formatUsd(
                            ratio(
                                row.original.tracked_estimated_spend_usd,
                                row.original.outreach_count,
                            ),
                        )}
                    </div>
                ),
            },
        ],
        [trackedOutreachCount],
    );
    return (
        <SpendTableCard
            title="Outreaches by caregiver response"
            description="Each outreach appears once: at least one yes, responses without a yes, or no recorded response. A yes does not mean the shift was filled."
            empty="No outreach response outcomes in this range."
            rows={trackedRows}
            columns={columns}
            note={
                excludedOutreachCount === 0
                    ? undefined
                    : `${excludedOutreachCount.toLocaleString()} historical ${excludedOutreachCount === 1 ? 'outreach' : 'outreaches'} excluded because response or shift data wasn’t captured.`
            }
        />
    );
}

export function OutreachStatusesTable({
    rows,
}: {
    readonly rows: Array<LlmSpendOutreachStatusRow>;
}) {
    const columns = useMemo<Array<ColumnDef<LlmSpendOutreachStatusRow>>>(
        () => [
            {
                accessorKey: 'status',
                header: 'Outcome',
                cell: ({ row }) => (
                    <div className="flex flex-col whitespace-normal">
                        <span className="font-medium">
                            {OUTCOME_DETAILS[row.original.status]?.label ??
                                statusLabel(row.original.status)}
                        </span>
                        <span className="text-muted-foreground max-w-xl text-xs">
                            {OUTCOME_DETAILS[row.original.status]?.description}
                        </span>
                    </div>
                ),
            },
            {
                accessorKey: 'outreach_count',
                header: () => <div className="text-right">Outreaches</div>,
                cell: ({ row }) => (
                    <div className="text-right tabular-nums">
                        {row.original.outreach_count.toLocaleString()}
                    </div>
                ),
            },
            {
                accessorKey: 'filled_shift_count',
                header: () => <div className="text-right">Shifts filled</div>,
                cell: ({ row }) => (
                    <div className="text-right tabular-nums">
                        {row.original.filled_shift_count.toLocaleString()} /{' '}
                        {row.original.shift_count.toLocaleString()}
                    </div>
                ),
            },
            {
                accessorKey: 'tracked_estimated_spend_usd',
                header: () => <div className="text-right">Total cost</div>,
                cell: ({ row }) => (
                    <div className="text-right tabular-nums">
                        {formatUsd(row.original.tracked_estimated_spend_usd)}
                    </div>
                ),
            },
            {
                id: 'cost_per_outreach',
                header: () => <div className="text-right">Per outreach</div>,
                cell: ({ row }) => (
                    <div className="text-right tabular-nums">
                        {formatUsd(
                            ratio(
                                row.original.tracked_estimated_spend_usd,
                                row.original.outreach_count,
                            ),
                        )}
                    </div>
                ),
            },
        ],
        [],
    );
    return (
        <SpendTableCard
            title="Outreaches by outcome"
            description="Terminal outreaches created in the range, as of the latest rollup. Shifts filled counts assignments from any source, not only Phoebe, over the shifts in those outreaches."
            empty="No finished outreaches in this range."
            rows={rows}
            columns={columns}
        />
    );
}

export function SchedulerOrganizationsTable({
    rows,
}: {
    readonly rows: Array<LlmSpendSchedulerOrganizationRow>;
}) {
    const columns = useMemo<Array<ColumnDef<LlmSpendSchedulerOrganizationRow>>>(
        () => [
            {
                accessorKey: 'organization_name',
                header: 'Organization',
                cell: ({ row }) => (
                    <OrganizationName
                        id={row.original.organization_id}
                        name={row.original.organization_name}
                        internalName={row.original.organization_internal_name}
                    />
                ),
            },
            {
                accessorKey: 'tracked_estimated_spend_usd',
                header: () => <div className="text-right">Spend</div>,
                cell: ({ row }) => (
                    <div className="text-right tabular-nums">
                        {formatUsd(row.original.tracked_estimated_spend_usd)}
                    </div>
                ),
            },
            {
                accessorKey: 'conversation_count',
                header: () => <div className="text-right">Conversations</div>,
                cell: ({ row }) => (
                    <div className="text-right tabular-nums">
                        {row.original.conversation_count.toLocaleString()}
                    </div>
                ),
            },
            {
                id: 'cost_per_conversation',
                header: () => (
                    <div className="text-right">Per conversation</div>
                ),
                cell: ({ row }) => (
                    <div className="text-right tabular-nums">
                        {formatUsd(
                            ratio(
                                row.original.tracked_estimated_spend_usd,
                                row.original.conversation_count,
                            ),
                        )}
                    </div>
                ),
            },
        ],
        [],
    );
    return (
        <SpendTableCard
            title="Scheduler organizations"
            description="Organizations with scheduler chat spend, highest first."
            empty="No scheduler organization spend in this range."
            rows={rows}
            columns={columns}
            paginate={true}
        />
    );
}

export function SchedulerUsersTable({
    rows,
}: {
    readonly rows: Array<LlmSpendSchedulerUserRow>;
}) {
    return (
        <UserTable rows={rows} totalUserCount={rows.length} scheduler={true} />
    );
}

export function SchedulerOutliersTable({
    rows,
}: {
    readonly rows: Array<LlmSpendSchedulerOutlierRow>;
}) {
    const columns = useMemo<Array<ColumnDef<LlmSpendSchedulerOutlierRow>>>(
        () => [
            {
                accessorKey: 'organization_name',
                header: 'Conversation',
                cell: ({ row }) => (
                    <div className="flex flex-col whitespace-normal">
                        <span className="font-medium">
                            {row.original.organization_name}
                        </span>
                        <span className="text-muted-foreground text-xs tabular-nums">
                            {formatDateAndTimeWithTz(
                                row.original.started_at,
                                LLM_SPEND_TIMEZONE,
                            )}
                        </span>
                    </div>
                ),
            },
            {
                accessorKey: 'tracked_estimated_spend_usd',
                header: () => <div className="text-right">Cost</div>,
                cell: ({ row }) => (
                    <div className="text-right tabular-nums">
                        {formatUsd(row.original.tracked_estimated_spend_usd)}
                    </div>
                ),
            },
            {
                id: 'activity',
                header: () => <div className="text-right">Activity</div>,
                cell: ({ row }) => (
                    <div className="text-right tabular-nums">
                        <div>{row.original.llm_call_count} calls</div>
                        <div className="text-muted-foreground text-xs">
                            {row.original.wake_count} wakes
                        </div>
                    </div>
                ),
            },
            {
                id: 'trace',
                header: '',
                cell: ({ row }) => (
                    <TraceLink id={row.original.conversation_id} />
                ),
            },
        ],
        [],
    );
    return (
        <SpendTableCard
            title="Scheduler outlier conversations"
            description="Scheduler conversations over $10, most expensive first."
            empty="No scheduler conversations over $10 in this range."
            rows={rows}
            columns={columns}
        />
    );
}

function SpendTableCard<Row extends RowData>({
    title,
    description,
    empty,
    rows,
    columns,
    paginate = false,
    note,
}: {
    readonly title: string;
    readonly description?: string;
    readonly empty: string;
    readonly rows: Array<Row>;
    readonly columns: Array<ColumnDef<Row>>;
    readonly paginate?: boolean;
    readonly note?: string;
}) {
    return (
        <Card>
            <CardHeader>
                <CardTitle className="text-sm">{title}</CardTitle>
                {description === undefined ? null : (
                    <CardDescription className="text-xs">
                        {description}
                    </CardDescription>
                )}
            </CardHeader>
            <CardContent>
                {rows.length === 0 ? (
                    <TableEmpty>{empty}</TableEmpty>
                ) : (
                    <DataTable
                        columns={columns}
                        data={rows}
                        pageSize={paginate ? 10 : 20}
                        hidePagination={!paginate}
                    />
                )}
                {note === undefined ? null : (
                    <p className="text-muted-foreground mt-2 text-xs">{note}</p>
                )}
            </CardContent>
        </Card>
    );
}
